import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { View, ViewProps } from 'tamagui';

import { GridsterContext } from './context';
import { GridsterEngine } from './core/gridster-engine';
import { GridsterItemConfig, GridsterOptions } from './types';

import './gridster.css';

export type GridsterProps = ViewProps & {
  options: GridsterOptions;
  onItemChange?: (items: GridsterItemConfig[]) => void;
  children?: ReactNode;
};

export const Gridster: React.FC<GridsterProps> = ({
  options,
  onItemChange,
  children,
  className,
  ...rest
}) => {
  const [engine] = useState(() => new GridsterEngine(options, onItemChange));
  const [rootEl, setRootEl] = useState<HTMLElement | null>(null);
  const [previewEl, setPreviewEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    engine.setOnItemChange(onItemChange);
  }, [engine, onItemChange]);

  useEffect(() => {
    engine.setOptions(options);
  }, [engine, options]);

  useLayoutEffect(() => {
    if (!rootEl || !previewEl) {
      return;
    }

    engine.attach(rootEl, previewEl);

    return () => engine.detach();
  }, [engine, rootEl, previewEl]);

  return (
    <GridsterContext value={engine}>
      <View
        ref={setRootEl as any}
        className={className ? `gridster ${className}` : 'gridster'}
        {...rest}
      >
        {children}
        <div ref={setPreviewEl} className="gridster-preview" />
      </View>
    </GridsterContext>
  );
};

Gridster.displayName = 'Gridster';
